import React, { lazy } from "react";
import { Redirect } from "react-router-dom";
import Staff from "../pages/staff/staff.js";
import UpdatePassword from "../pages/staff/changePassword.js";
import CreateStaff from "../pages/staff/createStaff.js";
import InviteScientist from "../pages/staff/InviteScientist.jsx";
import ScientistInvites from "../pages/staff/ScientistInvites.jsx";
import UpdateStaff from "../pages/staff/UpdateStaff.js";
import PondNote from "../pages/pondNote/pondNote.js";
import PondDetail from "../pages/pondDetail/pondDetail.js";
import CreatePondDetail from "../pages/pondDetail/CreadPD.js";
import UpdatePD from "../pages/pondDetail/UpdatePD.js";
import Pond from "../pages/pond/pond.js";
import CreatePond from "../pages/pond/createPond.js";
import UpdatePond from "../pages/pond/updatePond.js";
import Sampling from "../pages/sampling/sampling.js";
import PondFishes from "../pages/pondFish/pondFish.js";
import AddSamplingDetail from "../pages/sampling/AddsamplingDetail.js";
import PondDetailViaPond from "../pages/pondDetail/pondDetailViaPond.js";
import PondFishesViaPondDetail from "../pages/pondFish/pondFishViaPondDetail.js";
import SamplingDetailViaPondDetail from "../pages/sampling/SamplingDetailViaPondDetail.js";
import UpdateSamplingDetail from "../pages/sampling/updateSamplingDetail.js";
import SamplingGalleryViaPondDetail from "../pages/sampling/SamplingGalleryViaPondDetail.js";
import PondNoteViaPondDetail from "../pages/pondNote/pondNoteviaPondDetail.js";
import PondSamplingUpload from "../pages/pondDetail/PondSamplingUpload.js";
import ChoosePanel from "../pages/package/package.js";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Profile from "../pages/staff/profile.jsx";
import ScientistPond from "../pages/staff/ScientistPonds.jsx";
import Dashboard from "../pages/dashboard/dashboard.js";
import CompanyAccounts from "../pages/company/companyAccounts.js";
import CreateCompanyAccount from "../pages/company/createCompanyAccount.js";
import CompanyAccountDetail from "../pages/company/companyAccountDetail.js";
import CompanyAccountRenew from "../pages/company/companyAccountRenew.js";
import CompanyExtraPondPayment from "../pages/company/companyExtraPondPayment.js";
import CompanyStaff from "../pages/company/companyStaff.js";
import JoinConsultancy from "../pages/company/joinConsultancy.js";
import {
  isCompanyManagerUser,
  isCompanyManagedSamplingOnlyUser,
  isSuperUserSession,
} from "../components/content/axiosConfig.js";
import { isActingAsScopedAccount } from "../utils/accountContext.js";

function HomeRedirect() {
  if (isCompanyManagedSamplingOnlyUser()) {
    return <Redirect to="/fishingData" />;
  }
  if (isActingAsScopedAccount() && (isCompanyManagerUser() || isSuperUserSession())) {
    return <Redirect to="/dashboard" />;
  }
  if (isCompanyManagerUser()) {
    return <Redirect to="/company/accounts" />;
  }
  return <Redirect to="/dashboard" />;
}

const PondFeedFertilization = lazy(() =>
  import("../pages/pond/pondFeedFertilization.js")
);
const StockingData = lazy(() => import("../pages/pond/stockingData.js"));
const DashboardSampledFishPage = lazy(() =>
  import("../pages/dashboard/dashboardDrilldown.js").then((m) => ({
    default: m.DashboardSampledFishPage,
  }))
);
const DashboardFishTypesPage = lazy(() =>
  import("../pages/dashboard/dashboardDrilldown.js").then((m) => ({
    default: m.DashboardFishTypesPage,
  }))
);
const DashboardSamplingEventsPage = lazy(() =>
  import("../pages/dashboard/dashboardDrilldown.js").then((m) => ({
    default: m.DashboardSamplingEventsPage,
  }))
);

const FishDataTableView = lazy(() => import("../pages/sampling/TableView.jsx"));
const FishDataGraphView = lazy(() =>
  import("../pages/sampling/graphView/FishDataGraphView.jsx")
);
const FishDataGraphViewV2 = lazy(() =>
  import("../pages/sampling/graphViewV2/FishDataGraphViewV2.jsx")
);
const Payment = lazy(() => import("../pages/package/payment.js"));
const ExtraPondPayment = lazy(() => import("../pages/package/extraPondPayment.js"));

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    "pk_live_51TcWrnEFp6E1oRogJ6ul5KZwwOONiiQlsq1WfQiz6KFzwtF6k4ktPHC1ykVZiWZd67Wl2S9UA71VlHxMEJBddZmL00RkevipOk"
);

const routes = [
  {
    path: "/",
    exact: true,
    component: () => <HomeRedirect />,
  },
  {
    path: "/company/accounts/create",
    exact: true,
    title: "Create Company Account",
    component: () => <CreateCompanyAccount />,
  },
  {
    path: "/company/accounts/:id/renew",
    exact: true,
    title: "Renew Account",
    component: () => <CompanyAccountRenew />,
  },
  {
    path: "/company/accounts/:id/extra-pond",
    exact: true,
    title: "Buy Extra Pond",
    component: () => <CompanyExtraPondPayment />,
  },
  {
    path: "/company/accounts/:id",
    exact: true,
    title: "Company Account",
    component: () => <CompanyAccountDetail />,
  },
  {
    path: "/company/accounts",
    exact: true,
    title: "Company Accounts",
    component: () => <CompanyAccounts />,
  },
  {
    path: "/join-consultancy",
    exact: true,
    title: "Join a consultancy",
    component: () => <JoinConsultancy />,
  },
  {
    path: "/company/staff/:fisherId",
    exact: true,
    title: "Company Staff",
    component: () => <CompanyStaff />,
  },
  {
    path: "/dashboard/sampled-fish",
    title: "Total Sampled Fish",
    component: () => <DashboardSampledFishPage />,
  },
  {
    path: "/dashboard/fish-types",
    title: "Fish Types",
    component: () => <DashboardFishTypesPage />,
  },
  {
    path: "/dashboard/sampling-events",
    title: "Sampling Events",
    component: () => <DashboardSamplingEventsPage />,
  },
  {
    path: "/dashboard",
    exact: true,
    title: "Dashboard",
    component: () => <Dashboard />,
  },
  {
    path: "/pond",
    title: "Pond",
    component: () => <Pond />,
  },
  {
    path: "/createPond",
    title: "Create Pond",
    component: () => <CreatePond />,
  },
  {
    path: "/updatePond/:id",
    title: "Update Pond",
    component: () => <UpdatePond />,
  },
  {
    path: "/pondFeedFertilization/:id",
    title: "Feed & Fertilization",
    component: () => <PondFeedFertilization />,
  },
  {
    path: "/pondStocking/:id",
    title: "Stocking Data",
    component: () => <StockingData />,
  },
  {
    path: "/changePassword/:id",
    title: "Change Password",
    component: () => <UpdatePassword />,
  },
  {
    path: "/sampling",
    title: "Sampling",
    component: () => <Sampling />,
  },
  {
    path: "/samplingViaPondDetail/:id",
    title: "Sampling Via Pond Detail",
    component: () => <SamplingDetailViaPondDetail />,
  },
  {
    path: "/samplingGalleryViaPondDetail/:id",
    title: "Sampling Gallery",
    component: () => <SamplingGalleryViaPondDetail />,
  },
  {
    path: "/AddSamplingDetail/:id",
    title: "Add Sampling Detail",
    component: () => <AddSamplingDetail />,
  },
  {
    path: "/FishDataTableView/:id",
    title: "Fish Data Table ",
    component: () => <FishDataTableView />,
  },
  {
    path: "/FishDataTableView",
    exact: true,
    title: "Fish Data Table ",
    component: () => <FishDataTableView />,
  },
  {
    path: "/fish-data-graph/:pondDetailId",
    title: "Fish Data Graph",
    component: () => <FishDataGraphView />,
  },
  {
    path: "/fish-data-graph",
    exact: true,
    title: "Fish Data Graph",
    component: () => <FishDataGraphView />,
  },
  {
    path: "/fish-data-graph-new/:pondDetailId",
    title: "Fish Data Graph (New)",
    component: () => <FishDataGraphViewV2 />,
  },
  {
    path: "/fish-data-graph-new",
    exact: true,
    title: "Fish Data Graph (New)",
    component: () => <FishDataGraphViewV2 />,
  },
  {
    path: "/FishDataGraphView/:id",
    component: ({ match }) => (
      <Redirect to={`/fish-data-graph/${match.params.id}`} />
    ),
  },
  {
    path: "/FishDataGraphView",
    exact: true,
    component: () => <Redirect to="/fish-data-graph" />,
  },
  {
    path: "/UpdateSamplingDetail/:id",
    title: "Update Sampling Detail",
    component: () => <UpdateSamplingDetail />,
  },
  {
    path: "/fishTypes",
    title: "Fish Types",
    component: () => <Redirect to="/dashboard/fish-types" />,
  },
  {
    path: "/pondNote",
    title: "",
    component: () => <PondNote />,
  },
  {
    path: "/pondNoteViaPondDetail/:id",
    title: "",
    component: () => <PondNoteViaPondDetail />,
  },
  {
    path: "/pondFish",
    title: "pond Fish",
    component: () => <PondFishes />,
  },
  {
    path: "/pondFishViaPondDetail/:id",
    title: "pond Fish Via Pond Detail",
    component: () => <PondFishesViaPondDetail />,
  },

  {
    path: "/staff",
    title: "Users",
    component: () => <Staff />,
  },
  {
    path: "/createStaff",
    title: "Create Staff",
    component: () => <CreateStaff />,
  },
  {
    path: "/inviteScientist",
    title: "Invite Scientist",
    component: () => <InviteScientist />,
  },
  {
    path: "/scientistInvites",
    title: "Invitations",
    component: () => <ScientistInvites />,
  },
  {
    path: "/UpdateStaff/:id",
    title: "Update Staff",
    component: () => <UpdateStaff />,
  },
   {
    path: "/ScientistPond/:id",
    title: "Scientist Ponds",
    component: () => <ScientistPond />,
  },
  {
    path: "/fishingData",
    title: "Pond Detail",
    component: () => <PondDetail />,
  },
  {
    path: "/pondDetailViaPond/:id",
    title: "Pond Detail Via Pond",
    component: () => <PondDetailViaPond />,
  },
  {
    path: "/pondSamplingUpload/:pondId",
    title: "Upload Sampling Data",
    component: () => <PondSamplingUpload />,
  },
  {
    path: "/createpd/:id?",
    title: "Create Pond Detail",
    component: () => <CreatePondDetail />,
  },
  {
    path: "/UpdatePD/:id",
    title: "Update Pond Detail",
    component: () => <UpdatePD />,
  },
  {
    path: "/Updatepd/:id",
    title: "Update Pond Detail",
    component: ({ match }) => (
      <Redirect to={`/UpdatePD/${match.params.id}`} />
    ),
  },
  {
    path: "/packages",
    exact: true,
    title: "Packages",
    component: () => <ChoosePanel />,
  },
  {
    path: "/profile",
    title: "profile",
    component: () => <Profile />,
  },
  {
    path: "/payment/extra-pond",
    exact: true,
    title: "Extra Pond Payment",
    component: () => (
      <Elements stripe={stripePromise}>
        <ExtraPondPayment />
      </Elements>
    ),
  },
  {
    path: "/payment/:id",
    exact: true,
    title: "Payment",
    component: ({ match }) => {
      return (
        <Elements stripe={stripePromise}>
          <Payment id={match.params.id} />
        </Elements>
      );
    },
  },
];

export default routes;
